import {
  BROAD_SHIFT_SPECIALTY_IDS,
  SPECIALTY_RELATIONS,
} from "./specialtyRelationsCatalog";
import {
  inferSkillIdFromAnyLabel,
  resolveSkillLevelDisplayLabel,
  type SkillLevelNamingState,
} from "@/constants/skillLevelStateNaming";
import { getSkillLevelNamingState } from "./skillLevelNamingRuntime";

export type AcceptCriterionRateType = "perHour" | "contracted";

export type AcceptCriterion = {
  specialtyId: string;
  minSkillLevelId: string;
  /** Optional — when omitted the shift day rate applies (legacy). */
  rateType?: AcceptCriterionRateType;
  ratePerHour?: number | null;
  contractedRate?: number | null;
};

/** Display / AI queue rows — ids optional when names are present. */
export type AcceptCriteriaDisplay = {
  specialtyId?: string;
  minSkillLevelId?: string;
  specialtyName?: string;
  minSkillLevelName?: string;
  rateType?: AcceptCriterionRateType;
  ratePerHour?: number | null;
  contractedRate?: number | null;
};

export const coalesceAcceptCriteriaDisplay = (
  rows: AcceptCriteriaDisplay[] | undefined,
): AcceptCriteriaDisplay[] => {
  if (!rows?.length) return [];
  return rows.filter(
    (row) =>
      row &&
      (row.specialtyId || row.specialtyName) &&
      (row.minSkillLevelId || row.minSkillLevelName),
  );
};

export const formatAcceptCriterionRate = (
  row: Pick<
    AcceptCriterion,
    "rateType" | "ratePerHour" | "contractedRate"
  >,
): string | null => {
  if (row.rateType === "contracted" && row.contractedRate != null) {
    return `$${row.contractedRate}/day`;
  }
  if (row.rateType === "perHour" && row.ratePerHour != null) {
    return `$${row.ratePerHour}/hr`;
  }
  if (row.contractedRate != null) {
    return `$${row.contractedRate}/day`;
  }
  if (row.ratePerHour != null) {
    return `$${row.ratePerHour}/hr`;
  }
  return null;
};

/** Strip empty rate fields so legacy API payloads stay unchanged. */
export const normalizeAcceptCriterionRates = (
  criteria: AcceptCriterion[],
): AcceptCriterion[] =>
  criteria.map((row) => {
    const hasRate =
      (row.rateType === "perHour" &&
        row.ratePerHour != null &&
        row.ratePerHour > 0) ||
      (row.rateType === "contracted" &&
        row.contractedRate != null &&
        row.contractedRate > 0);
    if (!hasRate) {
      const { rateType, ratePerHour, contractedRate, ...rest } = row;
      return rest;
    }
    if (row.rateType === "perHour") {
      return {
        ...row,
        contractedRate: undefined,
        ratePerHour: Number(row.ratePerHour),
      };
    }
    if (row.rateType === "contracted") {
      return {
        ...row,
        ratePerHour: undefined,
        contractedRate: Number(row.contractedRate),
      };
    }
    return row;
  });

type Option = { value: string; label: string };

/** StatDoctor menu IDs (sp-*, sk-*) — never show these raw in the UI. */
export const looksLikeMenuId = (value?: string | null): boolean => {
  if (!value) return false;
  const v = value.trim();
  return /^(sp|sk)-\d+$/i.test(v);
};

export const labelForSpecialty = (
  specialtyId: string | undefined,
  specialtyName: string | undefined,
  specialtyOptions: Option[],
): string => {
  if (specialtyName && !looksLikeMenuId(specialtyName)) {
    return specialtyName;
  }
  if (specialtyId) {
    const byId = specialtyOptions.find((o) => o.value === specialtyId);
    if (byId?.label) return byId.label;
    if (!looksLikeMenuId(specialtyId)) return specialtyId;
  }
  return "";
};

export const labelForSkill = (
  skillId: string | undefined,
  skillName: string | undefined,
  skillOptions: Option[],
  namingState?: SkillLevelNamingState,
): string => {
  const state = namingState ?? getSkillLevelNamingState();
  const resolvedId =
    skillId ||
    (skillName && looksLikeMenuId(skillName) ? skillName : undefined) ||
    inferSkillIdFromAnyLabel(skillName);

  if (resolvedId) {
    const byId = skillOptions.find((o) => o.value === resolvedId);
    if (byId?.label) return byId.label;
    const fromState = resolveSkillLevelDisplayLabel({
      skillId: resolvedId,
      canonicalName: skillName,
      namingState: state,
    });
    if (fromState) return fromState;
  }

  if (skillName && !looksLikeMenuId(skillName)) {
    return skillName;
  }
  if (skillId && !looksLikeMenuId(skillId)) return skillId;
  return "";
};

export const enrichAcceptCriteriaForDisplay = (
  criteria: AcceptCriteriaDisplay[] | undefined,
  specialtyOptions: Option[],
  skillOptions: Option[],
  namingState?: SkillLevelNamingState,
): AcceptCriteriaDisplay[] => {
  if (!criteria?.length) return [];
  return criteria
    .map((row) => {
      const specialtyName = labelForSpecialty(
        row.specialtyId,
        row.specialtyName,
        specialtyOptions,
      );
      const minSkillLevelName = labelForSkill(
        row.minSkillLevelId,
        row.minSkillLevelName,
        skillOptions,
        namingState,
      );
      if (!specialtyName || !minSkillLevelName) return null;
      return {
        ...row,
        specialtyName,
        minSkillLevelName,
      };
    })
    .filter(Boolean) as AcceptCriteriaDisplay[];
};

const findSkillByHint = (options: Option[], hints: string[]): string => {
  const lowerHints = hints.map((h) => h.toLowerCase());
  for (const opt of options) {
    const label = opt.label.toLowerCase();
    if (lowerHints.some((hint) => label.includes(hint))) {
      return opt.value;
    }
  }
  return "";
};

const findSpecialtyByHint = (options: Option[], hints: string[]): string => {
  const lowerHints = hints.map((h) => h.toLowerCase());
  for (const opt of options) {
    const label = opt.label.toLowerCase();
    if (lowerHints.some((hint) => label.includes(hint))) {
      return opt.value;
    }
  }
  return "";
};

/** Domain pairing rules: GP -> PGY4+, Emergency -> VMO/SMO+, etc. */
export const defaultMinSkillForSpecialty = (
  specialtyId: string,
  specialtyOptions: Option[],
  skillOptions: Option[],
  fallbackSkillId: string,
): string => {
  const label =
    specialtyOptions.find((o) => o.value === specialtyId)?.label?.toLowerCase() ||
    "";

  if (label.includes("general practice")) {
    return findSkillByHint(skillOptions, ["pgy4", "pgy 4"]) || fallbackSkillId;
  }
  if (label.includes("emergency")) {
    return (
      findSkillByHint(skillOptions, ["vmo", "smo", "vmo/smo"]) ||
      fallbackSkillId
    );
  }
  return fallbackSkillId;
};

export const resolveSpecialtyId = (
  value: string,
  specialtyOptions: Option[],
): string => {
  if (!value) return "";
  const byId = specialtyOptions.find((o) => o.value === value);
  if (byId) return byId.value;
  const lower = value.toLowerCase();
  const byLabel = specialtyOptions.find((o) => o.label.toLowerCase() === lower);
  return byLabel?.value || value;
};

export const resolveSkillId = (
  value: string,
  skillOptions: Option[],
): string => {
  if (!value) return "";
  const byId = skillOptions.find((o) => o.value === value);
  if (byId) return byId.value;
  const lower = value.toLowerCase();
  const byLabel = skillOptions.find((o) => o.label.toLowerCase() === lower);
  return byLabel?.value || value;
};

export const dedupeAcceptCriteria = (
  criteria: AcceptCriterion[],
): AcceptCriterion[] => {
  const seen = new Set<string>();
  const out: AcceptCriterion[] = [];
  for (const row of criteria) {
    if (!row.specialtyId || !row.minSkillLevelId) continue;
    const key = `${row.specialtyId}|${row.minSkillLevelId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return normalizeAcceptCriterionRates(out);
};

/** acceptCriteria is "also open to" — never duplicate the shift primary pair. */
export const excludePrimaryFromAcceptCriteria = (
  criteria: AcceptCriterion[],
  primarySpecialtyId: string,
  primarySkillId: string,
): AcceptCriterion[] => {
  if (!primarySpecialtyId || !primarySkillId || !criteria.length) {
    return criteria;
  }
  const primaryKey = `${primarySpecialtyId}|${primarySkillId}`;
  return criteria.filter(
    (row) => `${row.specialtyId}|${row.minSkillLevelId}` !== primaryKey,
  );
};

export const buildAcceptCriteriaFromPrefill = (args: {
  primarySpecialtyId: string;
  primarySkillId: string;
  specialtyAlternatives?: string[];
  skillLevelAlternatives?: string[];
  acceptCriteria?: AcceptCriterion[];
  specialtyOptions: Option[];
  skillOptions: Option[];
}): AcceptCriterion[] => {
  const primarySpecId = resolveSpecialtyId(
    args.primarySpecialtyId,
    args.specialtyOptions,
  );
  const primarySkillId = resolveSkillId(args.primarySkillId, args.skillOptions);

  if (args.acceptCriteria?.length) {
    return excludePrimaryFromAcceptCriteria(
      dedupeAcceptCriteria(args.acceptCriteria),
      primarySpecId,
      primarySkillId,
    );
  }

  const criteria: AcceptCriterion[] = [];

  for (const spec of args.specialtyAlternatives || []) {
    const specialtyId = resolveSpecialtyId(spec, args.specialtyOptions);
    if (!specialtyId) continue;
    const minSkillLevelId = defaultMinSkillForSpecialty(
      specialtyId,
      args.specialtyOptions,
      args.skillOptions,
      primarySkillId,
    );
    if (minSkillLevelId) {
      criteria.push({ specialtyId, minSkillLevelId });
    }
  }

  for (const skill of args.skillLevelAlternatives || []) {
    const minSkillLevelId = resolveSkillId(skill, args.skillOptions);
    if (!primarySpecId || !minSkillLevelId || minSkillLevelId === primarySkillId) {
      continue;
    }
    criteria.push({ specialtyId: primarySpecId, minSkillLevelId });
  }

  return dedupeAcceptCriteria(criteria);
};

type MenuOption = { value: string; label: string };

export type EligibilityRow = AcceptCriterion & {
  /** First role from specialty + skill selectors (stored as shift primary in API). */
  isAnchor?: boolean;
};

export const eligibilityRowKey = (row: Pick<AcceptCriterion, "specialtyId" | "minSkillLevelId">) =>
  `${row.specialtyId}|${row.minSkillLevelId}`;

/** All roles shown equally in UI — anchor first, then also-open-to rows. */
export const buildEligibilityRows = (
  primarySpecialtyId: string,
  primarySkillId: string,
  acceptCriteria: AcceptCriterion[],
): EligibilityRow[] => {
  const rows: EligibilityRow[] = [];
  if (primarySpecialtyId && primarySkillId) {
    rows.push({
      specialtyId: primarySpecialtyId,
      minSkillLevelId: primarySkillId,
      isAnchor: true,
    });
  }
  const seen = new Set(rows.map(eligibilityRowKey));
  for (const row of acceptCriteria) {
    if (!row.specialtyId || !row.minSkillLevelId) continue;
    const key = eligibilityRowKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push(row);
  }
  return rows;
};

export type AcceptCriteriaSuggestionGroups = {
  related: AcceptCriterion[];
  broad: AcceptCriterion[];
};

const collectExistingEligibilityKeys = (
  anchorSpecialtyId: string,
  anchorSkillId: string,
  acceptCriteria: AcceptCriterion[],
): Set<string> =>
  new Set(
    buildEligibilityRows(anchorSpecialtyId, anchorSkillId, acceptCriteria).map(
      eligibilityRowKey,
    ),
  );

export const selectedIncludesBroadShiftSpecialty = (
  anchorSpecialtyId: string,
  acceptCriteria: AcceptCriterion[],
): boolean => {
  const selected = new Set([
    anchorSpecialtyId,
    ...acceptCriteria.map((row) => row.specialtyId),
  ]);
  return BROAD_SHIFT_SPECIALTY_IDS.some((id) => selected.has(id));
};

export const suggestAcceptCriteriaGroups = (args: {
  anchorSpecialtyId: string;
  anchorSkillId: string;
  acceptCriteria: AcceptCriterion[];
  specialtyOptions: MenuOption[];
  skillOptions: MenuOption[];
  relatedLimit?: number;
  broadLimit?: number;
}): AcceptCriteriaSuggestionGroups => {
  const {
    anchorSpecialtyId,
    anchorSkillId,
    acceptCriteria,
    specialtyOptions,
    skillOptions,
    relatedLimit = 6,
    broadLimit = 4,
  } = args;

  if (!anchorSpecialtyId || !anchorSkillId) {
    return { related: [], broad: [] };
  }

  const existingKeys = collectExistingEligibilityKeys(
    anchorSpecialtyId,
    anchorSkillId,
    acceptCriteria,
  );
  const validSpecIds = new Set(specialtyOptions.map((o) => o.value));
  const related: AcceptCriterion[] = [];
  const broad: AcceptCriterion[] = [];
  const seenRelated = new Set<string>();
  const seenBroad = new Set<string>();

  const tryAdd = (
    bucket: AcceptCriterion[],
    seen: Set<string>,
    specId: string,
    skillId: string,
    limit: number,
  ) => {
    if (bucket.length >= limit) return;
    if (!validSpecIds.has(specId) || !skillId) return;
    const key = eligibilityRowKey({ specialtyId: specId, minSkillLevelId: skillId });
    if (existingKeys.has(key) || seen.has(key)) return;
    seen.add(key);
    bucket.push({ specialtyId: specId, minSkillLevelId: skillId });
  };

  for (const specId of SPECIALTY_RELATIONS[anchorSpecialtyId] || []) {
    tryAdd(
      related,
      seenRelated,
      specId,
      defaultMinSkillForSpecialty(
        specId,
        specialtyOptions,
        skillOptions,
        anchorSkillId,
      ),
      relatedLimit,
    );
  }

  if (!selectedIncludesBroadShiftSpecialty(anchorSpecialtyId, acceptCriteria)) {
    for (const specId of BROAD_SHIFT_SPECIALTY_IDS) {
      tryAdd(
        broad,
        seenBroad,
        specId,
        defaultMinSkillForSpecialty(
          specId,
          specialtyOptions,
          skillOptions,
          anchorSkillId,
        ),
        broadLimit,
      );
    }
  }

  return { related, broad };
};

/** @deprecated Prefer suggestAcceptCriteriaGroups for related + broad buckets. */
export const suggestRelatedAcceptCriteria = (args: {
  anchorSpecialtyId: string;
  anchorSkillId: string;
  acceptCriteria: AcceptCriterion[];
  specialtyOptions: MenuOption[];
  skillOptions: MenuOption[];
  limit?: number;
}): AcceptCriterion[] =>
  suggestAcceptCriteriaGroups({
    ...args,
    relatedLimit: args.limit ?? 6,
    broadLimit: 0,
  }).related;
